import React, { useCallback, useEffect, useState } from "react";
import { Form, Input, Select, notification } from "antd";
import { FaRegIdBadge } from "react-icons/fa";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import api from "../../../../service";
import generateHeaders from "../../../../utils/generateHeaders";
import credentialTypesConstants from "../../../../constants/credentialTypes";
import {
  StyledFormItem,
  StyledInput,
  StyledSelect,
} from "../../../../components/ModalNewCollaborator/styles";
import {
  BonusCredentialModalContainer,
  ButtonFormConfirm,
  FooterContainer,
  FormRow,
  HeaderModal,
  ModalSubTitle,
  ModalTitle,
} from "./styles";

const OBSERVATION_MAX_LENGTH = 200;
const MIN_QUANTITY = 1;
const MAX_QUANTITY = 1000;

const { Option } = Select;
const { TextArea } = Input;

/**
 * INFORMACP-4818: bonificação de credenciais sem sair da jornada Staff.
 * O contexto (evento, oportunidade, conta e prestador) é derivado do atendimento no backend —
 * aqui só é enviado o id do atendimento junto com os dados do formulário.
 */
const BonusCredentialModal = ({
  open,
  setOpen,
  allowedCredentialTypes,
  onSuccess,
}) => {
  const intl = useIntl();
  const [bonusForm] = Form.useForm();
  const { token } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [laborUnions, setLaborUnions] = useState([]);

  const fantasyName = localStorage.getItem("serviceFantasyname");
  const serviceContractNumber = localStorage.getItem("serviceContractNumber");

  const credentialTypes = credentialTypesConstants();
  // Mesma regra da tela: enquanto a cota não carregou (null) não há o que oferecer.
  const availableCredentialTypes = allowedCredentialTypes
    ? credentialTypes.filter((item) =>
        allowedCredentialTypes.includes(item.value)
      )
    : [];

  // Traduz o código devolvido pela API quando existir; senão usa a mensagem do Jira. Evita o
  // clássico "api.error.undefined" quando a falha não traz corpo.
  const resolveErrorMessage = useCallback(
    (error) => {
      const fallback = intl.formatMessage({ id: "bonus_credential_error" });
      const code = error?.response?.data?.code;

      if (!code) {
        return fallback;
      }

      return intl.formatMessage({
        id: `api.error.${code}`,
        defaultMessage: fallback,
      });
    },
    [intl]
  );

  // Os motivos são praticamente estáticos: carrega uma vez e reaproveita entre aberturas.
  // O endpoint já devolve { label, value }, formato esperado pelo Select.
  useEffect(() => {
    if (!open || laborUnions.length) {
      return;
    }

    api
      .get("/bonus-credential/labor-union", generateHeaders(token))
      .then((result) => {
        setLaborUnions(result.data?.laborUnionOutput || []);
      })
      .catch((error) => {
        notification.error({
          message: intl.formatMessage({ id: "error" }),
          description: resolveErrorMessage(error),
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  const closeModal = useCallback(() => {
    // Um rascunho abandonado não deve reaparecer na próxima abertura.
    bonusForm.resetFields();
    setLoading(false);
    setOpen(false);
  }, [bonusForm, setOpen]);

  const onFinish = useCallback(
    async (values) => {
      const staffServiceId = localStorage.getItem("serviceId");

      try {
        setLoading(true);

        await api.post(
          "/bonus-credential/staff-journey",
          {
            staffServiceId: Number(staffServiceId),
            credentialType: values.credentialType,
            quantity: Number(values.quantity),
            laborUnionId: values.reason,
            reason: values.observation,
          },
          generateHeaders(token)
        );

        notification.success({
          message: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: "success.bonus_credential_released",
          }),
        });

        closeModal();

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        notification.error({
          message: intl.formatMessage({ id: "error" }),
          description: resolveErrorMessage(error),
        });
        setLoading(false);
      }
    },
    [closeModal, intl, onSuccess, resolveErrorMessage, token]
  );

  const requiredRule = [
    {
      required: true,
      message: intl.formatMessage({ id: "error.field_required" }),
    },
  ];

  const quantityRules = [
    ...requiredRule,
    {
      pattern: /^[0-9]+$/,
      message: intl.formatMessage({ id: "error.need_to_be_number" }),
    },
    {
      validator: (_, value) => {
        if (!value) {
          return Promise.resolve();
        }

        const quantity = Number(value);

        if (quantity < MIN_QUANTITY) {
          return Promise.reject(
            intl.formatMessage(
              { id: "error.min_value" },
              { value: MIN_QUANTITY }
            )
          );
        }

        if (quantity > MAX_QUANTITY) {
          return Promise.reject(
            intl.formatMessage(
              { id: "error.max_value" },
              { value: MAX_QUANTITY }
            )
          );
        }

        return Promise.resolve();
      },
    },
  ];

  return (
    <BonusCredentialModalContainer
      width={490}
      height={"auto"}
      closable
      onCancel={closeModal}
      visible={open}
      footer={""}
      destroyOnClose
      centered={true}
    >
      <HeaderModal>
        <div className="icon">
          <FaRegIdBadge
            style={{ fontSize: "18px", color: "rgba(6, 142, 195, 1)" }}
          />
        </div>
        <ModalTitle>
          {intl.formatMessage({ id: "bonus_credential_modal_title" })}
        </ModalTitle>
      </HeaderModal>

      <ModalSubTitle>
        {[fantasyName, serviceContractNumber].filter(Boolean).join(" • ")}
      </ModalSubTitle>

      <br />
      <br />

      <Form form={bonusForm} onFinish={onFinish} layout="vertical">
        <StyledFormItem
          label={intl.formatMessage({ id: "credential_type" })}
          name="credentialType"
          rules={requiredRule}
        >
          <StyledSelect
            dropdownMatchSelectWidth
            placeholder={intl.formatMessage({ id: "select" })}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {availableCredentialTypes.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </StyledSelect>
        </StyledFormItem>

        <FormRow>
          <StyledFormItem
            label={intl.formatMessage({ id: "quantity" })}
            name="quantity"
            rules={quantityRules}
          >
            <StyledInput
              type="number"
              min={MIN_QUANTITY}
              max={MAX_QUANTITY}
              step={1}
              placeholder={intl.formatMessage({ id: "quantity" })}
            />
          </StyledFormItem>

          <StyledFormItem
            label={intl.formatMessage({ id: "reason" })}
            name="reason"
            rules={requiredRule}
          >
            <StyledSelect
              dropdownMatchSelectWidth
              placeholder={intl.formatMessage({ id: "select" })}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {laborUnions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </StyledSelect>
          </StyledFormItem>
        </FormRow>

        <StyledFormItem
          label={intl.formatMessage({ id: "observation" })}
          name="observation"
          rules={requiredRule}
        >
          <TextArea
            rows={4}
            maxLength={OBSERVATION_MAX_LENGTH}
            showCount
            placeholder={intl.formatMessage({ id: "describe_bonus_reason" })}
          />
        </StyledFormItem>
      </Form>

      <FooterContainer>
        <ButtonFormConfirm onClick={closeModal} style={{ border: "none" }}>
          {intl.formatMessage({ id: "cancel" })}
        </ButtonFormConfirm>

        <ButtonFormConfirm
          loading={loading}
          onClick={() => bonusForm.submit()}
          style={{
            backgroundColor: "rgba(6, 142, 195, 1)",
            color: "rgba(255, 255, 255, 1)",
          }}
        >
          {intl.formatMessage({ id: "save" })}
        </ButtonFormConfirm>
      </FooterContainer>
    </BonusCredentialModalContainer>
  );
};

export default BonusCredentialModal;
